import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useState } from "react";
import { X } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import CanonicalTag from "@/components/CanonicalTag";

const HowToUseADUMultigenerationalHome = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <CanonicalTag url="https://www.udaadu.com/how-to-use-adu-multigenerational-home" />
      <Navigation />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-background to-muted py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-8 text-[hsl(var(--brand-primary))]">
            How to Use Your ADU as a Multi-Generational Home
          </h1>
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-[hsl(var(--brand-primary))]">
            Bring Generations Together — Without Sacrificing Privacy
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-12">
            Today's families are rethinking the way we live together. Whether you're caring for aging parents or making room for adult children, an ADU provides independence, dignity, and connection—all under one property line.
          </p>
          
          {/* Top CTA Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            <Button 
              onClick={openCalendlyModal}
              variant="secondary" 
              size="lg" 
              className="bg-[hsl(var(--brand-primary))] text-white hover:bg-[hsl(var(--brand-primary))]/90"
            >
              📅 Book & Schedule ➝
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-[hsl(var(--brand-primary))] text-white hover:bg-[hsl(var(--brand-primary))]/90"
            >
              <a 
                href="https://www.udaadu.com/landing" 
                className="w-full"
              >
                📐 What fits my lot? ➝
              </a>
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-[hsl(var(--brand-primary))] text-white hover:bg-[hsl(var(--brand-primary))]/90 md:col-span-2 lg:col-span-1"
            >
              <a 
                href="https://www.udaadu.com/adu-calculator" 
                className="w-full"
              >
                💰 Get your price & ROI report ➝
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Multi-Generational Photos Section */}
      <section className="py-16 px-4 bg-muted/50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <img 
                  src="/lovable-uploads/68f369b3-08e6-4c0d-a567-8ab08c9c089a.png" 
                  alt="Exterior of ADU showing proximity to main house"
                  className="h-48 w-full object-cover"
                />
              </CardContent>
            </Card>
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <img 
                  src="/lovable-uploads/55e33f9c-6e09-4ed6-b66c-2764cff17804.png" 
                  alt="Warm residential interior with natural light"
                  className="h-48 w-full object-cover"
                />
              </CardContent>
            </Card>
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <img 
                  src="/lovable-uploads/0402f4be-93a9-4e6f-83c9-f390ea2aa5b6.png" 
                  alt="Accessible entryway with step-free access"
                  className="h-48 w-full object-cover"
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Perfect Setup Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8 text-[hsl(var(--brand-primary))]">
            The Perfect Setup for Aging Parents or Boomerang Kids
          </h2>
          <p className="text-lg text-muted-foreground text-center mb-12">
            An ADU offers everyone their own kitchen, bathroom, and private entrance—while being just steps away for shared meals, caregiving, or peace of mind.
          </p>
          
          <div className="text-center mb-8">
            <h3 className="text-xl font-semibold mb-6">Great for:</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Elderly Parents Who Want Independence</h3>
                <p className="text-muted-foreground">
                  Maintain dignity and autonomy while ensuring family support is always nearby when needed.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Grandparents Living Near Grandkids</h3>
                <p className="text-muted-foreground">
                  Build precious memories with regular interaction while respecting everyone's personal space.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Adult Children Working or Studying from Home</h3>
                <p className="text-muted-foreground">
                  Provide a launching pad for young adults to establish independence while staying connected to family.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Family Members with Special Needs</h3>
                <p className="text-muted-foreground">
                  Create accessible, supportive living arrangements that promote independence and family care.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Design Features Section */}
      <section className="py-16 px-4 bg-muted/50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8 text-[hsl(var(--brand-primary))]">
            Design Features That Support Every Generation
          </h2>
          <p className="text-lg text-muted-foreground text-center mb-12">
            Our team helps customize your ADU with features like:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">No-Step Entries and Wide Doorways</h3>
                <p className="text-muted-foreground">
                  Barrier-free access ensures mobility for all ages and abilities, from toddlers to seniors with walkers.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Walk-In Showers and Easy-Grip Fixtures</h3>
                <p className="text-muted-foreground">
                  Safety-focused bathroom designs that accommodate aging in place and reduce fall risks.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Dual-Access Kitchens</h3>
                <p className="text-muted-foreground">
                  Inside and outside kitchen access facilitates shared family meals and independent cooking.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Sound-Insulated Walls</h3>
                <p className="text-muted-foreground">
                  Proper insulation ensures comfort and quiet for different schedules and lifestyle needs.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Legacy Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-8 text-[hsl(var(--brand-primary))]">
            More Than a House — It's a Legacy
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed mb-12">
            An ADU built for family is more than a living space. It's a way to stay close, share costs, and build multi-generational wealth together. Flexible zoning and ADU policies make it easier than ever to create a shared future at home.
          </p>
          
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardContent className="p-8">
                <h3 className="text-xl font-semibold mb-4">Multi-Generational Benefits</h3>
                <ul className="text-muted-foreground text-left space-y-3">
                  <li>• Shared household expenses and caregiving responsibilities</li>
                  <li>• Stronger family bonds and cultural continuity</li>
                  <li>• Increased property value and investment potential</li>
                  <li>• Flexible living arrangements that adapt over time</li>
                  <li>• Built-in support system for all family members</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Bottom Call-to-Action Section */}
      <section className="py-16 px-4 bg-[hsl(var(--brand-primary))] text-[hsl(var(--brand-primary-foreground))]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-8">Ready to Build Your Family Legacy?</h2>
          <p className="text-lg mb-12 opacity-90">
            Love grows stronger when families live with freedom and respect. Let UDA design an ADU that honors every generation—together, but independent.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Button 
              onClick={openCalendlyModal}
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90 w-full"
            >
              📅 Book & Schedule ➝
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90"
            >
              <a 
                href="https://www.udaadu.com/landing" 
                className="w-full"
              >
                📐 What fits my lot? ➝
              </a>
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90"
            >
              <a 
                href="https://www.udaadu.com/adu-calculator" 
                className="w-full"
              >
                💰 Get your price & ROI report ➝
              </a>
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90"
            >
              <a 
                href="https://www.udaadu.com/our-work" 
                className="w-full"
              >
                🏡 Our Portfolio ➝
              </a>
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90 md:col-span-2 lg:col-span-1"
            >
              <a 
                href="https://www.udaadu.com/faq" 
                className="w-full"
              >
                ❓ Our FAQ ➝
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button 
              onClick={closeCalendlyModal} 
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
            >
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe 
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" 
              width="100%" 
              height="100%" 
              frameBorder="0" 
              title="Schedule a meeting" 
            />
          </div>
        </div>
      )}
      <Footer />
      <FloatingADUWizard />
    </div>
  );
};

export default HowToUseADUMultigenerationalHome;